import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Activity, Search, Sparkles, Plus, Filter, Target, Users, Eye, Ear,
  Brain, Heart, ChevronRight, Video, AudioLines, Database,
} from 'lucide-react';
import { toast } from 'sonner';

const SKILL_META = {
  'توازن': { en: 'Balance', icon: Target, color: 'from-blue-500 to-cyan-500' },
  'قوة': { en: 'Strength', icon: Heart, color: 'from-rose-500 to-orange-500' },
  'مرونة': { en: 'Flexibility', icon: Activity, color: 'from-purple-500 to-pink-500' },
  'تآزر': { en: 'Coordination', icon: Sparkles, color: 'from-emerald-500 to-teal-500' },
  'إدراك مكاني': { en: 'Spatial', icon: Eye, color: 'from-amber-500 to-yellow-500' },
  'مهارة رياضية': { en: 'Sport-skill', icon: Activity, color: 'from-indigo-500 to-violet-500' },
};

const DISABILITY_META = {
  autism: { ar: 'توحد', en: 'Autism', icon: Brain },
  intellectual: { ar: 'ذهنية', en: 'Intellectual', icon: Brain },
  physical: { ar: 'حركية', en: 'Physical', icon: Activity },
  visual: { ar: 'بصرية', en: 'Visual', icon: Eye },
  hearing: { ar: 'سمعية', en: 'Hearing', icon: Ear },
};

export default function ExerciseLibraryPage() {
  const { t, lang, user } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [skill, setSkill] = useState('all');
  const [disability, setDisability] = useState('all');

  const canManage = user && ['teacher', 'admin'].includes(user.role);

  const reload = useCallback(() => {
    setLoading(true);
    const params = {};
    if (skill !== 'all') params.skill = skill;
    if (disability !== 'all') params.disability = disability;
    if (q) params.q = q;
    api.get('/exercises', { params }).then(r => setItems(r.data)).finally(() => setLoading(false));
  }, [skill, disability, q]);
  useEffect(() => { reload(); }, [reload]);

  const seed = async () => {
    try { const r = await api.post('/exercises/seed'); toast.success(lang === 'ar' ? `أُنشئ ${r.data.created} تمريناً` : `Seeded ${r.data.created}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'error'); }
  };

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="exercises-page">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-violet-50 to-emerald-50 p-6 sm:p-8 mb-6" data-testid="exercises-hero">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 grid place-items-center text-white shadow-lg">
              <Activity className="w-7 h-7" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-indigo-700 mb-1">{lang === 'ar' ? 'منصة همم' : 'Himam Platform'}</div>
              <h1 className="text-2xl sm:text-3xl font-black">{lang === 'ar' ? 'مكتبة التمارين الحركية الذكية' : 'Smart Movement Exercise Library'}</h1>
              <p className="text-sm text-muted-foreground mt-1">{lang === 'ar' ? 'تمارين معدّلة لذوي الإعاقة مع شرح، فيديو، نموذج 3D، توليد AI، وتحليل أداء.' : 'Adapted exercises with visuals, 3D, AI generator and performance review.'}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canManage && (
              <Button asChild className="bg-indigo-600 hover:bg-indigo-700" data-testid="open-generator">
                <Link to="/exercises/generate"><Sparkles className="w-4 h-4 me-1" />{lang === 'ar' ? 'مولّد AI' : 'AI Generator'}</Link>
              </Button>
            )}
            {canManage && (
              <Button asChild variant="outline" data-testid="open-analyzer">
                <Link to="/exercises/analyze"><Video className="w-4 h-4 me-1" />{lang === 'ar' ? 'تحليل فيديو' : 'Video Review'}</Link>
              </Button>
            )}
            {canManage && items.length === 0 && (
              <Button variant="outline" onClick={seed} data-testid="seed-btn"><Database className="w-4 h-4 me-1" />{lang === 'ar' ? 'بذر تجريبي' : 'Seed demo'}</Button>
            )}
          </div>
        </div>
      </section>

      {/* Filters */}
      <div className="bg-white border border-border rounded-xl p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === 'ar' ? 'بحث (الاسم، الهدف)...' : 'Search...'} className="ps-10" data-testid="exo-search" />
          </div>
        </div>
        <div className="flex items-center gap-2 mb-2 flex-wrap text-xs">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="font-bold text-muted-foreground">{lang === 'ar' ? 'المهارة:' : 'Skill:'}</span>
          <FilterPill active={skill === 'all'} onClick={() => setSkill('all')} label={lang === 'ar' ? 'الكل' : 'All'} testid="skill-all" />
          {Object.entries(SKILL_META).map(([ar, m]) => (
            <FilterPill key={ar} active={skill === ar} onClick={() => setSkill(ar)} label={lang === 'ar' ? ar : m.en} testid={`skill-${m.en.toLowerCase()}`} />
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Users className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="font-bold text-muted-foreground">{lang === 'ar' ? 'الإعاقة:' : 'Disability:'}</span>
          <FilterPill active={disability === 'all'} onClick={() => setDisability('all')} label={lang === 'ar' ? 'الكل' : 'All'} testid="disab-all" />
          {Object.entries(DISABILITY_META).map(([k, m]) => (
            <FilterPill key={k} active={disability === k} onClick={() => setDisability(k)} label={lang === 'ar' ? m.ar : m.en} testid={`disab-${k}`} />
          ))}
        </div>
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="text-center text-muted-foreground py-12" data-testid="exo-loading">{t('loading')}</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Activity className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm mb-3">{lang === 'ar' ? 'لا توجد تمارين. اضغط "بذر تجريبي" أو "مولّد AI".' : 'No exercises. Use "Seed demo" or "AI Generator".'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exo-grid">
          {items.map(exo => <ExerciseCard key={exo.id} exo={exo} lang={lang} />)}
        </div>
      )}
    </main>
  );
}

function FilterPill({ active, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${active ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white border-border hover:bg-secondary'}`}>
      {label}
    </button>
  );
}

function ExerciseCard({ exo, lang }) {
  const meta = SKILL_META[exo.skill_type_ar] || SKILL_META['تآزر'];
  const Icon = meta.icon;
  return (
    <Link to={`/exercises/${exo.id}`} className="block group" data-testid={`exo-card-${exo.id}`}>
      <Card className="overflow-hidden card-elevate h-full hover:border-indigo-300 transition-all">
        <div className={`h-24 bg-gradient-to-br ${meta.color} relative overflow-hidden flex items-center justify-center`}>
          <Icon className="w-12 h-12 text-white/90 group-hover:scale-110 transition-transform" aria-hidden="true" />
          <div className="absolute top-2 end-2">
            {exo.ai_generated && <Badge className="bg-white/95 text-indigo-700 border-0 text-[10px]"><Sparkles className="w-2.5 h-2.5 me-0.5" />AI</Badge>}
          </div>
          <div className="absolute bottom-2 start-2">
            <Badge className="bg-white/95 text-stone-800 border-0 text-[10px]">{lang === 'ar' ? exo.skill_type_ar : (exo.skill_type_en || exo.skill_type_ar)}</Badge>
          </div>
        </div>
        <CardContent className="p-4">
          <div className="font-bold text-sm mb-1 line-clamp-2 min-h-[2.5rem]">{lang === 'ar' ? exo.name_ar : (exo.name_en || exo.name_ar)}</div>
          <div className="text-xs text-muted-foreground line-clamp-2 mb-3 min-h-[2rem]">{lang === 'ar' ? exo.objective_ar : (exo.objective_en || exo.objective_ar)}</div>
          <div className="flex items-center gap-1 flex-wrap mb-2">
            {(exo.disability_fit || []).slice(0, 3).map(d => {
              const dm = DISABILITY_META[d];
              return dm ? <Badge key={d} variant="outline" className="text-[9px]">{lang === 'ar' ? dm.ar : dm.en}</Badge> : null;
            })}
          </div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{exo.age_min}–{exo.age_max} {lang === 'ar' ? 'سنة' : 'yrs'} · {exo.level}</span>
            <ChevronRight className="w-4 h-4 rtl:rotate-180 text-indigo-600" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
